import { deriveNaturalBackendOrigin } from '../_helpers/derive-origin';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { IStorage } from '../_interface/storage';

const prefix = 'crossDomainRequestID-';
/** Okvir sa app domena mora da se učita za ovoliko — bez mreže `load` možda nikad ne stigne.
 *  Velikodušno: spora (mobilna) veza ne sme da obori okvir koji bi se ipak učitao. */
const FRAME_LOAD_TIMEOUT_MS = 15000;
/** Okvir mora da odgovori na poruku za ovoliko — inače je u njemu npr. stranica greške. */
const REPLY_TIMEOUT_MS = 2000;

@Injectable({
  providedIn: 'root'
})
export class CrossDomainStorageService implements IStorage {
  _iFrame?: HTMLIFrameElement;
  _loadingFramePromise?: Promise<void>;

  private isLoaded = new BehaviorSubject<boolean>(false);
  private connected = false;
  private _callbacks: {
    [key: string]: {
      resolve: (value: any) => void,
      reject: (err: any) => void,
      timer: Subscription
    }
  } = {};
  // Cross-domain storage lives on the app's natural backend domain (where the iframe is
  // physically loaded from). It must NOT follow a runtime server-switch override —
  // deriveBackendOrigin() / environment.backendBase can change mid-session, but the
  // iframe's origin cannot, so the postMessage origin check is always against natural.
  private storageBase = deriveNaturalBackendOrigin().origin;
  private source = this.storageBase + "/storage";

  constructor() {
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  loadFrame(): Promise<void> {
    if (this._loadingFramePromise != null) {
      return this._loadingFramePromise;
    }

    const frame = document.createElement("iframe");
    this._iFrame = frame;

    this._loadingFramePromise = new Promise((resolve, reject) => {
      // Bez mreže `load` možda nikad ne stigne — odustani, pa sledeći poziv pravi novi okvir.
      const loadTimer = setTimeout(() => {
        this.resetFrame(frame);
        reject("storage not loaded");
      }, FRAME_LOAD_TIMEOUT_MS);

      frame.src = this.source;
      frame.style.display = 'none';
      frame.addEventListener("load", () => {
        clearTimeout(loadTimer);
        this.isLoaded.next(true)
        return resolve();
      });

      document.body.append(frame);
    })

    return this._loadingFramePromise;
  }

  /**
   * Odbacuje okvir koji ne odgovara. Učitan bez mreže, u njemu stoji stranica greške i tako bi
   * ostao do reload-a stranice — svaki kasniji get/set bi istekao i kad se veza vrati.
   * Sledeći poziv ga učitava iznova. Dira samo okvir koji je i dalje aktuelan (istovremeni
   * zahtev je možda već napravio novi).
   */
  private resetFrame(frame: HTMLIFrameElement) {
    if (frame !== this._iFrame) {
      return;
    }
    frame.remove();
    this._iFrame = undefined;
    this._loadingFramePromise = undefined;
    this.isLoaded.next(false);
  }

  handleMessage(event: any) {
    const response = event.data;
    const requestID = this.getId(response);

    if (event.origin !== this.storageBase) {
      return;
    }

    if (!requestID) {
      return;
    }

    var promise = this._callbacks[requestID];
    if (!promise) {
      return;
    }
    delete this._callbacks[requestID];
    promise.timer.unsubscribe();

    if (response.connectError) {
      promise.reject(response.error);
    } else {
      promise.resolve(response.data);
    }
  }

  private createID() {
    let length = 10;
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return prefix + "_" + result;
  }

  get(key: string) {
    var promiseResolve!: (value: any) => void;
    var promiseReject!: (err: any) => void;

    var promise = new Promise<any>((resolve, reject) => {
      promiseResolve = resolve;
      promiseReject = reject;
    });

    this.message('get', key, null, promiseResolve, promiseReject);

    return promise;
  }


  set(key: string, value: any) {

    var promiseResolve!: (value: any) => void;
    var promiseReject!: (err: any) => void;

    var promise = new Promise<any>((resolve, reject) => {
      promiseResolve = resolve;
      promiseReject = reject;
    });

    this.message('set', key, value, promiseResolve, promiseReject);

    return promise;
  }

  remove(key: string) {
    var promiseResolve!: (value: any) => void;
    var promiseReject!: (err: any) => void;

    var promise = new Promise<any>((resolve, reject) => {
      promiseResolve = resolve;
      promiseReject = reject;
    });

    this.message('remove', key, null, promiseResolve, promiseReject);

    return promise;
  }

  message(method: string, key: string, value: any, resolve: (value: unknown) => void, reject: (err: any) => void) {
    this.loadFrame().then(() => {
      const frame = this._iFrame;
      if (!frame) {
        // istovremeni zahtev je u međuvremenu odbacio okvir
        reject("storage not loaded");
        return;
      }
      const id = this.createID();

      this._callbacks[id] = {
        resolve: resolve,
        reject: reject,
        timer: timer(REPLY_TIMEOUT_MS).subscribe(() => {
          delete this._callbacks[id];
          // okvir ne odgovara (npr. učitan dok nije bilo mreže) — sledeći poziv ga učitava iznova
          this.resetFrame(frame);
          reject("timeout");
        })
      };

      frame.contentWindow?.postMessage(
        {
          method,
          key,
          value,
          id,
        },
        this.source,
      );
    }, err => reject(err));
  }

  getId(data: any) {
    let id;

    if (data && data.id && ~data.id.indexOf(prefix)) {
      id = data.id;
    }

    return id;
  }


}
